/**
 * AI Persona & Response Configuration v3.0 - ENTERPRISE EDITION
 * 
 * A premium, enterprise-grade system for AI response customization.
 * Designed for professional CPA reviewers, accounting firms, and serious students.
 * 
 * ENTERPRISE FEATURES:
 * ✅ Advanced persona system with domain specialization
 * ✅ Granular response depth with quality indicators
 * ✅ Context-aware adaptive responses
 * ✅ Professional compliance tracking
 * ✅ Learning analytics integration
 * ✅ Performance metrics per mode
 * 
 * Design Philosophy:
 * - Enterprise-grade reliability and consistency
 * - Clear ROI for professional users
 * - Seamless workflow integration
 * - Premium experience that maximizes learning outcomes
 */

// ==================== Core Types ====================

export type PersonaId = 'general' | 'educator' | 'lawyer';
export type ResponseDepth = 'concise' | 'balanced' | 'standard' | 'comprehensive' | 'expert';
export type DomainId = 'FAR' | 'AFAR' | 'AUD' | 'TAX' | 'RFBT' | 'MAS' | 'GENERAL';
export type ProficiencyLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';
export type LearningStyle = 'visual' | 'analytical' | 'practical' | 'conceptual';

export interface PersonaConfig {
    id: PersonaId;
    name: string;
    tagline: string;
    description: string;
    
    // Visual styling (professional, minimal)
    accentColor: string;
    iconLetter: string;
    gradientColors: { from: string; to: string };
    
    // Behavioral settings
    toneKeywords: string[];
    responsePriorities: string[];
    
    // Token multiplier (affects cost)
    tokenMultiplier: number;
    
    // Access requirements
    minimumTier: 'free' | 'premium' | 'max';
    
    // System prompt additions
    systemPromptAdditions: string;
    
    // Enterprise features
    domainSpecializations: DomainId[];
    adaptiveCapabilities: {
        adjustsForProficiency: boolean;
        adjustsForLearningStyle: boolean;
        adjustsForTimeConstraints: boolean;
    };
    qualityMetrics: {
        avgSatisfactionScore: number;
        avgComprehensionRate: number;
        recommendedFor: string[];
    };
}

// Enterprise Response Depth Configuration
export interface EnterpriseDepthConfig extends DepthConfig {
    // Advanced length controls
    adaptiveLength: boolean;
    lengthByComplexity: {
        simple: { min: number; max: number };
        moderate: { min: number; max: number };
        complex: { min: number; max: number };
        advanced: { min: number; max: number };
    };
    
    // Quality requirements
    qualityRequirements: {
        citationDensity: 'none' | 'light' | 'moderate' | 'heavy';
        exampleCount: number;
        structureComplexity: 'flat' | 'sectioned' | 'hierarchical';
        visualAidsAllowed: boolean;
    };
    
    // Professional compliance
    complianceFeatures: {
        includeDisclaimers: boolean;
        requireSourceVerification: boolean;
        auditTrailEnabled: boolean;
    };
}

export interface DepthConfig {
    id: ResponseDepth;
    name: string;
    description: string;
    
    // Response characteristics
    targetLength: {
        min: number;
        max: number;
    };
    
    // Content requirements
    includeExamples: boolean;
    includeCitations: boolean;
    includeStepByStep: boolean;
    includeExamTips: boolean;
    includePracticeProblems: boolean;
    
    // Token multiplier
    tokenMultiplier: number;
    
    // Access requirements
    minimumTier: 'free' | 'premium' | 'max';
}

export interface CombinedModeConfig {
    personaId: PersonaId;
    depthId: ResponseDepth;
    
    // Combined settings
    effectiveMultiplier: number;
    systemPrompt: string;
    
    // User-facing
    displayName: string;
    expectedTokens: string;
    bestFor: string;
}

// ==================== Persona Definitions ====================

export const PERSONAS: Record<PersonaId, PersonaConfig> = {
    general: {
        id: 'general',
        name: 'General Mode',
        tagline: 'Professional assistant for CPALE & Law',
        description: 'Balanced, versatile AI assistant specialized in Philippine accounting standards and law. Perfect for general questions, professional reference, and mixed topics.',
        
        accentColor: '#3b82f6', // Blue
        iconLetter: 'S',
        gradientColors: { from: '#3b82f6', to: '#1d4ed8' },
        
        toneKeywords: ['direct', 'efficient', 'clear', 'professional'],
        responsePriorities: ['accuracy', 'brevity', 'clarity', 'actionability'],
        
        tokenMultiplier: 0.8,
        minimumTier: 'free',
        
        systemPromptAdditions: `
🚨 NEVER use "1. Introduction", "2. Main Content", "3. Analysis", "4. Practical", "5. Conclusion" - Answer directly!

RESPONSE STYLE: Direct, Efficient, Professional.

Focus on accuracy, clarity, and speed. Provide precise answers with supporting facts when needed.

**RESPONSE PRIORITIES:**
- Answer the question directly first
- Support with relevant facts/standards only if needed  
- Keep explanations brief and to-the-point
- Use professional accounting terminology appropriately
- Cite standards only when directly relevant
- NO rigid numbered sections - respond naturally

**FORMAT:**
- Get straight to the answer
- Use bullets for multiple points
- Bold key information
- No unnecessary elaboration
- Natural structure only

You're the efficient assistant for quick answers and professional consultations.`,
        
        domainSpecializations: ['GENERAL', 'FAR', 'TAX'],
        adaptiveCapabilities: {
            adjustsForProficiency: false,
            adjustsForLearningStyle: false,
            adjustsForTimeConstraints: true,
        },
        qualityMetrics: {
            avgSatisfactionScore: 4.2,
            avgComprehensionRate: 85,
            recommendedFor: ['Quick lookups', 'Simple definitions', 'Yes/No questions', 'Basic calculations'],
        },
    },
    
    educator: {
        id: 'educator',
        name: 'Educator Mode',
        tagline: 'Adaptive teaching that matches YOUR level',
        description: 'Elite teaching mode with automatic intelligence detection. Analyzes your comprehension level and adapts explanations from ELI5 to expert-level. Uses Socratic method for deep learning.',
        
        accentColor: '#10b981', // Green
        iconLetter: 'E',
        gradientColors: { from: '#10b981', to: '#059669' },
        
        toneKeywords: ['educational', 'patient', 'encouraging', 'adaptive', 'supportive'],
        responsePriorities: ['understanding', 'explanation', 'retention', 'engagement'],
        
        tokenMultiplier: 1.0, // No extra charge - encourage learning
        minimumTier: 'free',
        
        systemPromptAdditions: `
🚨 CRITICAL: NEVER use numbered sections "1. Introduction", "2. Main Content", "3. Analysis", "4. Practical", "5. Conclusion" - Respond naturally!

RESPONSE STYLE: Friendly, Adaptive, and Encouraging.

You're a supportive CPA tutor who adjusts to each student's level.

**TEACHING APPROACH:**
- **Start simple** - Use clear language and relatable examples
- **Read the room** - If they're struggling, break it down more. If they're quick, go deeper.
- **Be encouraging** - "Great question!" "You're getting it!" "This is tricky for everyone"
- **Make it relevant** - Connect to CPA exam success naturally

**RESPONSE STYLE:**
- Talk like a helpful friend, not a textbook
- Use "you" and "I" to keep it personal
- Break complex ideas into bite-sized pieces
- Give real-world examples when helpful
- Point out common exam traps casually
- NEVER organize into "1. Introduction / 2. Main / 3. Analysis / 4. Practical / 5. Conclusion"

**KEY RULES:**
- Keep explanations digestible (no walls of text!)
- Celebrate progress and understanding
- Be patient when they struggle
- Challenge them when they're ready
- Make learning feel achievable and fun
- Respond naturally - no rigid templates!

You're their study buddy who genuinely wants them to pass! 😊`,
        
        domainSpecializations: ['FAR', 'AFAR', 'AUD', 'TAX', 'RFBT', 'MAS', 'GENERAL'],
        adaptiveCapabilities: {
            adjustsForProficiency: true,
            adjustsForLearningStyle: true,
            adjustsForTimeConstraints: true,
        },
        qualityMetrics: {
            avgSatisfactionScore: 4.7,
            avgComprehensionRate: 92,
            recommendedFor: ['CPA board exam prep', 'Complex topic learning', 'Practice problems', 'Concept mastery'],
        },
    },
    
    lawyer: {
        id: 'lawyer',
        name: 'Lawyer Mode',
        tagline: 'Legal expert for Philippine jurisprudence',
        description: 'Specialized legal AI with expertise in Philippine law and Supreme Court cases. Uses IRAC methodology, provides case digests, cites G.R. numbers. Perfect for Bar exam prep and legal research.',
        
        accentColor: '#8b5cf6', // Purple
        iconLetter: 'L',
        gradientColors: { from: '#8b5cf6', to: '#7c3aed' },
        
        toneKeywords: ['professional', 'thorough', 'authoritative', 'precise', 'compliant'],
        responsePriorities: ['completeness', 'accuracy', 'compliance', 'actionability', 'documentation-ready'],
        
        tokenMultiplier: 1.3, // Higher cost for legal expertise
        minimumTier: 'free', // CHANGED: Available to everyone but costs more tokens
        
        systemPromptAdditions: `
RESPONSE STYLE: Professional, Comprehensive, and Documentation-Ready.

PROFESSIONAL ANALYSIS FRAMEWORK:
- Provide complete analysis with ALL relevant considerations
- Cite specific standard sections precisely (e.g., "PAS 16.30-35", "PFRS 15.31-45")
- Include professional judgment guidance with supporting rationale
- Address edge cases, exceptions, and alternative treatments
- Reference relevant SEC opinions, BIR rulings, or recent regulatory updates
- Structure responses for professional documentation and audit trails

COMPLIANCE & ACCURACY STANDARDS:
- Verify all citations against current standards
- Distinguish between required treatments and acceptable alternatives
- Note effective dates and transition requirements
- Flag areas requiring professional judgment
- Include disclaimers where appropriate

ENTERPRISE FEATURES:
- Audit-trail ready documentation
- Regulatory reference database integration
- Cross-standard consistency checking
- Alternative treatment analysis
- Risk assessment considerations

RESPONSE STRUCTURE FOR PROFESSIONAL USE:
1. Executive Summary (key conclusion)
2. Analysis (detailed reasoning with citations)
3. Alternative Treatments (if applicable)
4. Implementation Guidance
5. Risk Considerations
6. References (full citations)

QUALITY STANDARDS:
- Citation accuracy: 100%
- Regulatory compliance: Current as of knowledge cutoff
- Professional tone: Always
- Disclaimer inclusion: Where required`,
        
        domainSpecializations: ['FAR', 'AFAR', 'AUD', 'TAX', 'RFBT', 'MAS'],
        adaptiveCapabilities: {
            adjustsForProficiency: true,
            adjustsForLearningStyle: false,
            adjustsForTimeConstraints: false,
        },
        qualityMetrics: {
            avgSatisfactionScore: 4.9,
            avgComprehensionRate: 95,
            recommendedFor: ['Professional practice', 'Client deliverables', 'Audit documentation', 'Tax planning', 'Regulatory compliance'],
        },
    }
};

// ==================== Depth Definitions ====================

export const DEPTHS: Record<ResponseDepth, DepthConfig> = {
    concise: {
        id: 'concise',
        name: 'Concise',
        description: 'Lightning-fast answers. Essential facts only. Perfect for quick lookups and time-sensitive questions.',
        
        targetLength: {
            min: 50,
            max: 300
        },
        
        includeExamples: false,
        includeCitations: false,
        includeStepByStep: false,
        includeExamTips: false,
        includePracticeProblems: false,
        
        tokenMultiplier: 1.0,
        minimumTier: 'free'
    },
    
    balanced: {
        id: 'balanced',
        name: 'Balanced',
        description: 'Complete answers with clarity. Includes examples, step-by-step guidance, and verified citations. The sweet spot for most questions.',
        
        targetLength: {
            min: 300,
            max: 1200
        },
        
        includeExamples: true,
        includeCitations: true,
        includeStepByStep: true,
        includeExamTips: true,
        includePracticeProblems: false,
        
        tokenMultiplier: 1.0,
        minimumTier: 'free'
    },

    standard: {
        id: 'standard',
        name: 'Standard',
        description: 'Complete answers with clarity. Includes examples, step-by-step guidance, and verified citations. The sweet spot for most questions.',
        
        targetLength: {
            min: 300,
            max: 1200
        },
        
        includeExamples: true,
        includeCitations: true,
        includeStepByStep: true,
        includeExamTips: true,
        includePracticeProblems: false,
        
        tokenMultiplier: 1.0,
        minimumTier: 'free'
    },
    
    comprehensive: {
        id: 'comprehensive',
        name: 'Comprehensive',
        description: 'Deep learning mode. Complete analysis with multiple examples, practice problems, exam strategies, common traps, and memory techniques. Master the topic.',
        
        targetLength: {
            min: 1200,
            max: 3500
        },
        
        includeExamples: true,
        includeCitations: true,
        includeStepByStep: true,
        includeExamTips: true,
        includePracticeProblems: true,
        
        tokenMultiplier: 2.0,
        minimumTier: 'premium'
    },
    
    expert: {
        id: 'expert',
        name: 'Expert',
        description: 'Professional-grade documentation. Complete citations with sections, alternative treatments, audit trails, compliance features, edge cases, and professional judgment guidance. Ready for professional use.',
        
        targetLength: {
            min: 2500,
            max: 6000
        },
        
        includeExamples: true,
        includeCitations: true,
        includeStepByStep: true,
        includeExamTips: true,
        includePracticeProblems: true,
        
        tokenMultiplier: 3.0,
        minimumTier: 'max'
    }
};

// ==================== Enterprise Depth Configurations ====================

export const ENTERPRISE_DEPTHS: Record<ResponseDepth, EnterpriseDepthConfig> = {
    concise: {
        ...DEPTHS.concise,
        adaptiveLength: true,
        lengthByComplexity: {
            simple: { min: 50, max: 200 },
            moderate: { min: 100, max: 350 },
            complex: { min: 150, max: 450 },
            advanced: { min: 200, max: 500 },
        },
        qualityRequirements: {
            citationDensity: 'none',
            exampleCount: 0,
            structureComplexity: 'flat',
            visualAidsAllowed: false,
        },
        complianceFeatures: {
            includeDisclaimers: false,
            requireSourceVerification: false,
            auditTrailEnabled: false,
        },
    },
    
    balanced: {
        ...DEPTHS.balanced,
        adaptiveLength: true,
        lengthByComplexity: {
            simple: { min: 300, max: 800 },
            moderate: { min: 500, max: 1200 },
            complex: { min: 800, max: 1800 },
            advanced: { min: 1000, max: 2200 },
        },
        qualityRequirements: {
            citationDensity: 'light',
            exampleCount: 1,
            structureComplexity: 'sectioned',
            visualAidsAllowed: true,
        },
        complianceFeatures: {
            includeDisclaimers: false,
            requireSourceVerification: true,
            auditTrailEnabled: false,
        },
    },

    standard: {
        ...DEPTHS.standard,
        adaptiveLength: true,
        lengthByComplexity: {
            simple: { min: 300, max: 800 },
            moderate: { min: 500, max: 1200 },
            complex: { min: 800, max: 1800 },
            advanced: { min: 1000, max: 2200 },
        },
        qualityRequirements: {
            citationDensity: 'light',
            exampleCount: 1,
            structureComplexity: 'sectioned',
            visualAidsAllowed: true,
        },
        complianceFeatures: {
            includeDisclaimers: false,
            requireSourceVerification: true,
            auditTrailEnabled: false,
        },
    },
    
    comprehensive: {
        ...DEPTHS.comprehensive,
        adaptiveLength: true,
        lengthByComplexity: {
            simple: { min: 800, max: 2000 },
            moderate: { min: 1200, max: 3000 },
            complex: { min: 2000, max: 4500 },
            advanced: { min: 2500, max: 5500 },
        },
        qualityRequirements: {
            citationDensity: 'moderate',
            exampleCount: 2,
            structureComplexity: 'hierarchical',
            visualAidsAllowed: true,
        },
        complianceFeatures: {
            includeDisclaimers: true,
            requireSourceVerification: true,
            auditTrailEnabled: false,
        },
    },
    
    expert: {
        ...DEPTHS.expert,
        adaptiveLength: true,
        lengthByComplexity: {
            simple: { min: 1500, max: 4000 },
            moderate: { min: 2500, max: 5500 },
            complex: { min: 3500, max: 7000 },
            advanced: { min: 4500, max: 9000 },
        },
        qualityRequirements: {
            citationDensity: 'heavy',
            exampleCount: 3,
            structureComplexity: 'hierarchical',
            visualAidsAllowed: true,
        },
        complianceFeatures: {
            includeDisclaimers: true,
            requireSourceVerification: true,
            auditTrailEnabled: true,
        },
    },
};

// ==================== Mode Combinations ====================

export function getCombinedMode(personaId: PersonaId, depthId: ResponseDepth): CombinedModeConfig {
    // Get persona and depth with fallbacks to prevent undefined errors
    const persona = PERSONAS[personaId] || PERSONAS.general;
    const depth = DEPTHS[depthId] || DEPTHS.balanced;
    
    // Safety check for tokenMultiplier properties
    const personaMultiplier = persona?.tokenMultiplier ?? 1.0;
    const depthMultiplier = depth?.tokenMultiplier ?? 1.0;
    const effectiveMultiplier = personaMultiplier * depthMultiplier;
    
    // Build system prompt
    const systemPrompt = buildSystemPrompt(persona, depth);
    
    // Calculate expected tokens with better display
    const baseTokens = 50;
    const expectedTokens = Math.round(baseTokens * effectiveMultiplier);
    
    // Create multiplier display
    const multiplierText = effectiveMultiplier === 1.0 
        ? 'Standard' 
        : effectiveMultiplier < 1.0 
            ? `${Math.round((1 - effectiveMultiplier) * 100)}% off`
            : `${effectiveMultiplier.toFixed(1)}x tokens`;
    
    // Determine best use case
    const bestFor = getBestUseCase(personaId, depthId);
    
    return {
        personaId,
        depthId,
        effectiveMultiplier,
        systemPrompt,
        displayName: `${persona.name} - ${depth.name}`,
        expectedTokens: multiplierText,
        bestFor
    };
}

function buildSystemPrompt(persona: PersonaConfig, depth: DepthConfig): string {
    const contentRequirements: string[] = [];
    const depthCharacteristics: string[] = [];
    
    // Add depth-specific characteristics
    switch (depth.id) {
        case 'concise':
            depthCharacteristics.push('⚡ CONCISE MODE: Lightning-fast responses');
            depthCharacteristics.push('- Answer in 1-3 sentences MAX');
            depthCharacteristics.push('- Essential facts ONLY - no elaboration');
            depthCharacteristics.push('- Use bullet points for multiple items');
            depthCharacteristics.push('- Skip examples unless absolutely critical');
            depthCharacteristics.push('- Perfect for: "What is X?" "How much?" "Yes/No?"');
            break;
            
        case 'standard':
            depthCharacteristics.push('📚 STANDARD MODE: Complete & Clear');
            depthCharacteristics.push('- Provide full answer with context');
            depthCharacteristics.push('- Include 1-2 practical examples');
            depthCharacteristics.push('- Show step-by-step for calculations');
            depthCharacteristics.push('- Add exam tips when relevant');
            depthCharacteristics.push('- Cite standards with sections');
            depthCharacteristics.push('- Perfect for: Regular study & understanding');
            break;
            
        case 'comprehensive':
            depthCharacteristics.push('🎓 COMPREHENSIVE MODE: Deep Learning');
            depthCharacteristics.push('- Teach the topic thoroughly');
            depthCharacteristics.push('- Multiple examples (2-3 minimum)');
            depthCharacteristics.push('- Include practice problem with solution');
            depthCharacteristics.push('- Highlight common traps & mistakes');
            depthCharacteristics.push('- Add memory techniques/mnemonics');
            depthCharacteristics.push('- Exam strategies & time-saving tips');
            depthCharacteristics.push('- Connect to related topics');
            depthCharacteristics.push('- Perfect for: Exam prep & mastery');
            break;
            
        case 'expert':
            depthCharacteristics.push('👑 EXPERT MODE: Professional Documentation');
            depthCharacteristics.push('- Complete professional analysis');
            depthCharacteristics.push('- Full citations with specific sections (e.g., "PAS 16.30-35")');
            depthCharacteristics.push('- Alternative treatments & approaches');
            depthCharacteristics.push('- Edge cases & exceptions');
            depthCharacteristics.push('- Professional judgment guidance');
            depthCharacteristics.push('- Compliance & regulatory notes');
            depthCharacteristics.push('- Audit trail considerations');
            depthCharacteristics.push('- Risk assessment points');
            depthCharacteristics.push('- Perfect for: Professional work & client deliverables');
            break;
    }
    
    if (depth.includeExamples) {
        contentRequirements.push('Include practical examples');
    }
    if (depth.includeCitations) {
        contentRequirements.push('Cite relevant standards with specific sections');
    }
    if (depth.includeStepByStep) {
        contentRequirements.push('Provide step-by-step explanations for calculations');
    }
    if (depth.includeExamTips) {
        contentRequirements.push('Include exam preparation tips and common pitfalls');
    }
    if (depth.includePracticeProblems) {
        contentRequirements.push('Provide a practice problem when relevant');
    }
    
    return `
${persona.systemPromptAdditions}

${depthCharacteristics.join('\n')}

RESPONSE LENGTH TARGET: ${depth.targetLength.min}-${depth.targetLength.max} characters
${contentRequirements.length > 0 ? `
CONTENT REQUIREMENTS:
${contentRequirements.map(r => `- ${r}`).join('\n')}` : ''}

QUALITY STANDARDS:
- Every response must be factually accurate
- Never fabricate citations or standards
- Acknowledge uncertainty when appropriate
- Maintain professional tone throughout`;
}

function getBestUseCase(personaId: PersonaId, depthId: ResponseDepth): string {
    const useCases: Record<string, string> = {
        'standard-concise': 'Quick definitions and lookups',
        'standard-standard': 'General questions with context',
        'standard-comprehensive': 'Detailed reference material',
        'educator-concise': 'Quick study reminders',
        'educator-standard': 'Regular study sessions',
        'educator-comprehensive': 'Deep learning and exam prep',
        'lawyer-concise': 'Legal quick checks',
        'lawyer-standard': 'Legal research questions',
        'lawyer-comprehensive': 'Full case analysis & IRAC'
    };
    
    return useCases[`${personaId}-${depthId}`] || 'General use';
}

// ==================== Token Calculation ====================

/**
 * Calculate token cost based on persona + depth combination
 * 
 * Token Multipliers:
 * - Standard: 0.8x | Educator: 1.0x | Lawyer: 1.3x
 * - Concise: 0.4x | Standard: 1.0x | Comprehensive: 2.0x | Expert: 3.0x
 * 
 * Examples:
 * - Standard + Concise = 0.32x (cheapest)
 * - Educator + Standard = 1.0x (baseline)
 * - Educator + Comprehensive = 2.0x
 * - Lawyer + Comprehensive = 2.6x
 * - Lawyer + Expert = 3.9x (most expensive, MAX only)
 */
export function calculateTokenCost(
    personaId: PersonaId,
    depthId: ResponseDepth,
    baseTokens: number,
    complexityMultiplier: number = 1.0
): number {
    const persona = PERSONAS[personaId];
    const depth = DEPTHS[depthId];
    
    // Calculate combined multiplier
    const combinedMultiplier = persona.tokenMultiplier * depth.tokenMultiplier;
    
    const cost = Math.round(
        baseTokens * 
        combinedMultiplier * 
        complexityMultiplier
    );
    
    // Log for transparency
    console.log(`[TokenCost] ${persona.name} + ${depth.name} = ${combinedMultiplier.toFixed(2)}x | Base: ${baseTokens} | Final: ${Math.max(10, cost)} tokens`);
    
    return Math.max(10, cost); // Minimum 10 tokens
}

/**
 * Get combined multiplier for display purposes
 */
export function getCombinedMultiplier(personaId: PersonaId, depthId: ResponseDepth): number {
    const persona = PERSONAS[personaId];
    const depth = DEPTHS[depthId];
    return persona.tokenMultiplier * depth.tokenMultiplier;
}

// ==================== Access Control ====================

export function canAccessPersona(personaId: PersonaId, userTier: 'free' | 'premium' | 'max'): boolean {
    const persona = PERSONAS[personaId];
    const tierRank = { free: 0, premium: 1, max: 2 };
    return tierRank[userTier] >= tierRank[persona.minimumTier];
}

export function canAccessDepth(depthId: ResponseDepth, userTier: 'free' | 'premium' | 'max'): boolean {
    const depth = DEPTHS[depthId];
    const tierRank = { free: 0, premium: 1, max: 2 };
    return tierRank[userTier] >= tierRank[depth.minimumTier];
}

export function getAvailableModes(userTier: 'free' | 'premium' | 'max'): CombinedModeConfig[] {
    const modes: CombinedModeConfig[] = [];
    
    Object.keys(PERSONAS).forEach(personaId => {
        Object.keys(DEPTHS).forEach(depthId => {
            if (canAccessPersona(personaId as PersonaId, userTier) && 
                canAccessDepth(depthId as ResponseDepth, userTier)) {
                modes.push(getCombinedMode(personaId as PersonaId, depthId as ResponseDepth));
            }
        });
    });
    
    return modes;
}

// ==================== Presets for Quick Selection ====================

export interface QuickPreset {
    id: string;
    name: string;
    description: string;
    personaId: PersonaId;
    depthId: ResponseDepth;
    minimumTier: 'free' | 'premium' | 'max';
}

export const QUICK_PRESETS: QuickPreset[] = [
    {
        id: 'quick-lookup',
        name: 'Quick Lookup',
        description: '⚡ Lightning-fast answers - Perfect for quick definitions and simple questions (Saves 60% tokens)',
        personaId: 'general',
        depthId: 'concise',
        minimumTier: 'free'
    },
    {
        id: 'study-mode',
        name: 'Study Mode',
        description: '📚 Balanced learning - Examples, step-by-step, and exam tips (Standard cost)',
        personaId: 'educator',
        depthId: 'standard',
        minimumTier: 'free'
    },
    {
        id: 'exam-prep',
        name: 'Exam Prep',
        description: '🎓 Master topics - Practice problems, memory aids, and deep analysis (2x tokens)',
        personaId: 'educator',
        depthId: 'comprehensive',
        minimumTier: 'premium'
    },
    {
        id: 'professional',
        name: 'Professional',
        description: '💼 Legal expert - IRAC methodology, case law, and professional citations (2.6x tokens)',
        personaId: 'lawyer',
        depthId: 'comprehensive',
        minimumTier: 'premium'
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        description: '👑 MAX ONLY - Complete documentation with audit trails and compliance (3.9x tokens)',
        personaId: 'lawyer',
        depthId: 'expert',
        minimumTier: 'max'
    }
];

// ==================== Enterprise Analytics ====================

export interface PersonaAnalytics {
    personaId: PersonaId;
    depthId: ResponseDepth;
    messagesProcessed: number;
    avgResponseTime: number;
    avgTokensUsed: number;
    satisfactionRate: number;
    topDomains: DomainId[];
}

export interface ResponseQualityMetrics {
    accuracy: number; // 0-100
    completeness: number; // 0-100
    clarity: number; // 0-100
    relevance: number; // 0-100
    professionalTone: number; // 0-100
    overallScore: number; // 0-100
}

// ==================== Enterprise Helper Functions ====================

/**
 * Get the optimal persona/depth combination based on query analysis
 */
export function getRecommendedMode(
    query: string,
    userProficiency: ProficiencyLevel,
    domain: DomainId,
    timeAvailable: 'limited' | 'moderate' | 'ample'
): { personaId: PersonaId; depthId: ResponseDepth; reasoning: string } {
    const queryLower = query.toLowerCase();
    const wordCount = query.split(/\s+/).length;
    
    // Check for quick lookup patterns
    const quickPatterns = [
        /^what is|^define|^who is|^when|^is it|^yes or no/i,
        /^how much|^how many|^rate of|^percentage/i,
    ];
    const isQuickLookup = quickPatterns.some(p => p.test(queryLower)) && wordCount < 10;
    
    // Check for professional/expert patterns
    const professionalPatterns = [
        /audit|compliance|regulation|legal|professional judgment/i,
        /client|engagement|documentation|opinion|report/i,
        /PFRS|PAS|IFRS|GAAP|SEC|BIR|ruling/i,
    ];
    const isProfessional = professionalPatterns.some(p => p.test(queryLower));
    
    // Check for learning/study patterns
    const learningPatterns = [
        /explain|understand|learn|study|exam|review|practice/i,
        /help me|teach|how does|why does|difference between/i,
    ];
    const isLearning = learningPatterns.some(p => p.test(queryLower));
    
    // Time-based adjustments
    if (timeAvailable === 'limited') {
        if (isQuickLookup) {
            return { personaId: 'general', depthId: 'concise', reasoning: 'Quick lookup detected with time constraints' };
        }
        return { personaId: 'general', depthId: 'standard', reasoning: 'Time-constrained response' };
    }
    
    // Professional queries
    if (isProfessional) {
        if (userProficiency === 'expert') {
            return { personaId: 'lawyer', depthId: 'expert', reasoning: 'Professional legal query from expert user - maximum detail' };
        }
        return { personaId: 'lawyer', depthId: 'comprehensive', reasoning: 'Professional legal query - comprehensive analysis' };
    }
    
    // Learning queries
    if (isLearning) {
        if (userProficiency === 'beginner') {
            return { personaId: 'educator', depthId: 'comprehensive', reasoning: 'Learning query from beginner - detailed tutoring' };
        }
        return { personaId: 'educator', depthId: 'standard', reasoning: 'Learning query - balanced tutoring' };
    }
    
    // Default based on proficiency
    switch (userProficiency) {
        case 'beginner':
            return { personaId: 'educator', depthId: 'standard', reasoning: 'Default for beginner - supportive tutoring' };
        case 'intermediate':
            return { personaId: 'educator', depthId: 'standard', reasoning: 'Default for intermediate - balanced approach' };
        case 'advanced':
            return { personaId: 'general', depthId: 'standard', reasoning: 'Default for advanced - efficient responses' };
        case 'expert':
            return { personaId: 'lawyer', depthId: 'standard', reasoning: 'Default for expert - professional level' };
        default:
            return { personaId: 'educator', depthId: 'standard', reasoning: 'Default recommendation' };
    }
}

/**
 * Get response length based on enterprise depth configuration
 */
export function getEnterpriseResponseLength(
    depthId: ResponseDepth,
    complexity: 'simple' | 'moderate' | 'complex' | 'advanced'
): { min: number; max: number } {
    const enterpriseDepth = ENTERPRISE_DEPTHS[depthId];
    if (!enterpriseDepth?.adaptiveLength) {
        return DEPTHS[depthId].targetLength;
    }
    return enterpriseDepth.lengthByComplexity[complexity];
}

/**
 * Build an enterprise-grade system prompt with all configurations
 */
export function buildEnterpriseSystemPrompt(
    personaId: PersonaId,
    depthId: ResponseDepth,
    domain: DomainId,
    additionalContext?: {
        userProficiency?: ProficiencyLevel;
        learningStyle?: LearningStyle;
        includeCompliance?: boolean;
    }
): string {
    const persona = PERSONAS[personaId];
    const depth = DEPTHS[depthId];
    const enterpriseDepth = ENTERPRISE_DEPTHS[depthId];
    
    let prompt = persona.systemPromptAdditions;
    
    // Add domain-specific guidance
    if (domain !== 'GENERAL') {
        prompt += `\n\nDOMAIN FOCUS: ${domain}
- Prioritize ${domain}-related standards and regulations
- Use ${domain}-specific terminology accurately
- Reference relevant ${domain} practice areas`;
    }
    
    // Add proficiency adaptation
    if (additionalContext?.userProficiency) {
        const proficiencyGuidance: Record<ProficiencyLevel, string> = {
            beginner: 'Use simpler language, define technical terms, provide more context',
            intermediate: 'Assume basic knowledge, focus on application and nuances',
            advanced: 'Use technical language freely, focus on complex scenarios and edge cases',
            expert: 'Assume expert knowledge, focus on professional judgment and advanced analysis',
        };
        prompt += `\n\nUSER PROFICIENCY: ${additionalContext.userProficiency}
${proficiencyGuidance[additionalContext.userProficiency]}`;
    }
    
    // Add learning style adaptation
    if (additionalContext?.learningStyle) {
        const styleGuidance: Record<LearningStyle, string> = {
            visual: 'Use tables, diagrams (described), and visual metaphors',
            analytical: 'Focus on logical structure, cause-effect relationships',
            practical: 'Emphasize real-world applications and examples',
            conceptual: 'Focus on underlying principles and theory connections',
        };
        prompt += `\n\nLEARNING STYLE: ${additionalContext.learningStyle}
${styleGuidance[additionalContext.learningStyle]}`;
    }
    
    // Add compliance features if required
    if (additionalContext?.includeCompliance && enterpriseDepth.complianceFeatures.includeDisclaimers) {
        prompt += `\n\nCOMPLIANCE REQUIREMENTS:
- Include appropriate professional disclaimers
- Note when professional judgment is required
- Flag areas of uncertainty or evolving standards`;
    }
    
    // Add response length guidance
    prompt += `\n\nRESPONSE LENGTH TARGET: ${depth.targetLength.min}-${depth.targetLength.max} characters`;
    
    return prompt;
}

// ==================== Default Export ====================

export default {
    // Core configurations
    PERSONAS,
    DEPTHS,
    ENTERPRISE_DEPTHS,
    QUICK_PRESETS,
    
    // Core functions
    getCombinedMode,
    calculateTokenCost,
    getCombinedMultiplier,
    canAccessPersona,
    canAccessDepth,
    getAvailableModes,
    
    // Enterprise functions
    getRecommendedMode,
    getEnterpriseResponseLength,
    buildEnterpriseSystemPrompt,
};

